import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SafeheirLogo from './SafeheirLogo';

const DashboardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="7" height="7" rx="1.5"/>
    <rect x="10" y="1" width="7" height="7" rx="1.5"/>
    <rect x="1" y="10" width="7" height="7" rx="1.5"/>
    <rect x="10" y="10" width="7" height="7" rx="1.5"/>
  </svg>
);

const VaultIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="14" height="10" rx="2"/>
    <path d="M5 7V5a4 4 0 118 0v2"/>
    <circle cx="9" cy="12" r="1.5"/>
  </svg>
);

const BeneficiariesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6.5" cy="5.5" r="2.5"/>
    <path d="M1 16v-1.5A3.5 3.5 0 014.5 11h4A3.5 3.5 0 0112 14.5V16"/>
    <circle cx="13" cy="6" r="2"/>
    <path d="M14 11a3 3 0 012.5 3.5V16"/>
  </svg>
);

const CheckinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 1L2 5v4.5c0 4.5 3 7.5 7 8.5 4-1 7-4 7-8.5V5L9 1z"/>
    <path d="M6 9l2.5 2.5L13 7"/>
  </svg>
);

const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-safeheir-menu]')) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = () => {
    if (user?.full_name) {
      const parts = user.full_name.trim().split(/\s+/);
      return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].substring(0, 2).toUpperCase();
    }
    if (user?.email) return user.email.substring(0, 2).toUpperCase();
    return 'U';
  };

  const navigationItems = [
    { path: '/dashboard', label: 'Tableau de bord', icon: DashboardIcon },
    { path: '/vault', label: 'Coffre-fort', icon: VaultIcon },
    { path: '/beneficiaries', label: 'Bénéficiaires', icon: BeneficiariesIcon },
    { path: '/checkin', label: 'Check-in', icon: CheckinIcon },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06]"
        style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(16px)' }}>
        <div className="container mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <SafeheirLogo size="sm" />
          </Link>

          <div className="flex items-center space-x-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.path} to={item.path}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200"
                  style={{
                    background: isActive(item.path) ? 'rgba(0,245,160,0.1)' : 'transparent',
                    color: isActive(item.path) ? '#00F5A0' : '#8899AA',
                  }}
                  onMouseEnter={(e) => { if (!isActive(item.path)) { e.currentTarget.style.color = '#E8ECF1'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}}
                  onMouseLeave={(e) => { if (!isActive(item.path)) { e.currentTarget.style.color = '#8899AA'; e.currentTarget.style.background = 'transparent'; }}}
                >
                  <Icon />
                  <span className="font-medium text-sm">{item.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="relative flex items-center space-x-3" data-safeheir-menu>
            <button onClick={() => setShowMenu(!showMenu)}
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200"
              style={{ background: 'rgba(0,245,160,0.15)', color: '#00F5A0' }}>
              {getInitials()}
            </button>
            {showMenu && (
              <div className="absolute top-12 right-0 rounded-xl border border-white/[0.08] p-2 min-w-[200px]"
                style={{ background: '#111D2E' }}>
                <div className="px-3 py-2 border-b border-white/[0.06] mb-1">
                  <div className="text-sm text-white font-medium truncate">{user?.full_name || user?.email}</div>
                  <div className="text-xs truncate" style={{ color: '#8899AA' }}>{user?.email}</div>
                </div>
                <Link to="/settings"
                  onClick={() => setShowMenu(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-white/[0.05] no-underline"
                  style={{ color: '#8899AA' }}>
                  Paramètres
                </Link>
                <button onClick={handleLogout}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-white/[0.05]"
                  style={{ color: '#8899AA' }}>
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06]"
        style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(16px)' }}>
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <SafeheirLogo size="sm" />
          </Link>
          <button onClick={() => setShowMenu(!showMenu)}
            data-safeheir-menu
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
            style={{ background: 'rgba(0,245,160,0.15)', color: '#00F5A0' }}>
            {getInitials()}
          </button>
        </div>
      </div>

      {/* Mobile User Dropdown */}
      {showMenu && (
        <div className="md:hidden fixed top-14 right-0 z-50 m-3 rounded-xl border border-white/[0.08] p-2 min-w-[200px]"
          data-safeheir-menu
          style={{ background: '#111D2E' }}>
          <div className="px-3 py-2 border-b border-white/[0.06] mb-1">
            <div className="text-sm text-white font-medium truncate">{user?.full_name || user?.email}</div>
            <div className="text-xs truncate" style={{ color: '#8899AA' }}>{user?.email}</div>
          </div>
          <Link to="/settings"
            onClick={() => setShowMenu(false)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-white/[0.05] no-underline"
            style={{ color: '#8899AA' }}>
            Paramètres
          </Link>
          <button onClick={handleLogout}
            className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-white/[0.05]"
            style={{ color: '#8899AA' }}>
            Se déconnecter
          </button>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-white/[0.06]"
        style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(16px)' }}>
        <div className="grid grid-cols-4 gap-1 p-2">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path}
                className="flex flex-col items-center space-y-1 py-2 rounded-lg transition-all duration-200"
                style={{
                  color: isActive(item.path) ? '#00F5A0' : '#556677',
                  background: isActive(item.path) ? 'rgba(0,245,160,0.08)' : 'transparent',
                }}>
                <Icon />
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Navigation;
